import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type HTMLAttributes,
} from 'react';
import { cn } from '../../../lib/utils';

// Content is a bottom-sheet on mobile and a centered card on `lg`. The `size`
// variant only tunes the desktop max-width; the default (`md`) reproduces the
// legacy fixed `lg:max-w-[480px]` verbatim so existing dialogs don't shift.
const dialogContentVariants = cva(
  cn(
    'fixed z-50 flex max-h-[90vh] w-full flex-col gap-0 overflow-hidden border border-border bg-background p-0 shadow-md outline-none',
    'left-0 right-0 top-auto bottom-0 rounded-t-xl',
    'lg:left-[50%] lg:right-auto lg:top-[50%] lg:bottom-auto lg:max-h-[85vh] lg:w-full lg:translate-x-[-50%] lg:translate-y-[-50%] lg:rounded-xl'
  ),
  {
    variants: {
      size: {
        sm: 'lg:max-w-md',
        md: 'lg:max-w-[480px]',
        lg: 'lg:max-w-lg',
        xl: 'lg:max-w-2xl',
        '2xl': 'lg:max-w-3xl',
        '3xl': 'lg:max-w-4xl',
      },
    },
    defaultVariants: { size: 'md' },
  }
);

export type DialogContentSize = NonNullable<VariantProps<typeof dialogContentVariants>['size']>;

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn('fixed inset-0 z-50 bg-black/80 lg:bg-black/50', className)}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content> &
    VariantProps<typeof dialogContentVariants>
>(({ className, children, size, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(dialogContentVariants({ size }), className)}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

/**
 * Scrollable body region for a Dialog. Pair with sticky `DialogHeader` /
 * `DialogFooter` (both `shrink-0`) inside a `DialogContent`: the content is a
 * capped-height flex column, so this `flex-1 overflow-y-auto` region scrolls
 * while the header and footer stay pinned — no per-consumer `max-h`/`overflow`
 * class repetition.
 */
function DialogBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="dialog-body"
      className={cn('min-h-0 flex-1 overflow-y-auto', className)}
      {...props}
    />
  );
}
DialogBody.displayName = 'DialogBody';

function DialogHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex shrink-0 flex-col gap-1.5 text-left', className)}
      {...props}
    />
  );
}
DialogHeader.displayName = 'DialogHeader';

function DialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        'flex shrink-0 flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-2',
        className
      )}
      {...props}
    />
  );
}
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
